"use client"

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FiDollarSign, FiClock, FiCheckCircle, FiAlertCircle, FiDownload, FiCalendar } from 'react-icons/fi';
import { motion } from 'framer-motion';

const CommissionPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({
    totalEarned: 0,
    pendingPayout: 0,
    completedMissions: 0,
    nextPayoutDate: ''
  });

  // Simuler le chargement des données
  useEffect(() => {
    const fetchCommissionData = async () => {
      try {
        setLoading(true);
        // Simuler un appel API
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Données factices pour la démo
        const mockCommissions = [
          { id: 1, date: new Date('2023-10-15'), amount: 150.50, status: 'payé', missionId: 'M-2023-001' },
          { id: 2, date: new Date('2023-10-10'), amount: 89.99, status: 'en attente', missionId: 'M-2023-002' },
          { id: 3, date: new Date('2023-10-05'), amount: 210.00, status: 'payé', missionId: 'M-2023-003' },
        ];
        
        const totalEarned = mockCommissions
          .filter(c => c.status === 'payé')
          .reduce((sum, curr) => sum + curr.amount, 0);
          
        const pendingPayout = mockCommissions
          .filter(c => c.status === 'en attente')
          .reduce((sum, curr) => sum + curr.amount, 0);
          
        setCommissions(mockCommissions);
        setStats({
          totalEarned,
          pendingPayout,
          completedMissions: mockCommissions.length,
          nextPayoutDate: '2023-11-01'
        });
      } catch (error) {
        console.error('Erreur lors du chargement des commissions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCommissionData();
  }, [user?.id]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTND = (value) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-slate-700/50 rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-slate-800/50 rounded-2xl p-6 h-40 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-white">Mes Commissions</h1>
        <p className="text-slate-400">Gérez vos gains et vos paiements</p>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div 
          whileHover={{ y: -5 }}
          className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-300">Total gagné</p>
              <p className="text-2xl font-bold text-white mt-1">{formatTND(stats.totalEarned)}</p>
            </div>
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <FiDollarSign className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-500/20">
            <p className="text-xs text-blue-300">
              <FiClock className="inline mr-1" />
              Dernière mise à jour: {formatDate(new Date())}
            </p>
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ y: -5 }}
          className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-300">En attente de paiement</p>
              <p className="text-2xl font-bold text-white mt-1">{formatTND(stats.pendingPayout)}</p>
            </div>
            <div className="p-3 bg-amber-500/20 rounded-lg">
              <FiClock className="w-6 h-6 text-amber-400" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-amber-500/20">
            <p className="text-xs text-amber-300">
              <FiCalendar className="inline mr-1" />
              Prochain paiement: {formatDate(stats.nextPayoutDate)}
            </p>
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ y: -5 }}
          className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-300">Missions terminées</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.completedMissions}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <FiCheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-green-500/20">
            <p className="text-xs text-green-300">
              <FiAlertCircle className="inline mr-1" />
              Taux de commission: 15%
            </p>
          </div>
        </motion.div>
      </div>

      {/* Liste des commissions */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Historique des commissions</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-800/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Mission
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Montant
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Statut
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-800/30 divide-y divide-slate-700">
              {commissions.map((commission) => (
                <tr key={commission.id} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                    {formatDate(commission.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {commission.missionId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-400">
                    {formatTND(commission.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      commission.status === 'payé' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {commission.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-400 hover:text-blue-300">
                      <FiDownload className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="px-6 py-4 bg-slate-800/30 text-right">
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <FiDownload className="-ml-1 mr-2 h-4 w-4" />
            Exporter en PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommissionPage;
